import DashboardIcon from '@mui/icons-material/Dashboard';
import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { Provider } from 'jotai';
import {
  ApiError,
  AppNameProvider,
  ErrorFallback,
  Forbidden,
  Layout,
  Policy,
} from 'ui';
import { APP_NAME } from '../appName';
import '../sentry.client.config';
import '../styles/globals.css';
import theme from '../styles/theme';
import useUser from '../auth/useUser';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';
import toast, { Toaster } from 'react-hot-toast';
import { AppProps } from 'next/app';
import { useMemo } from 'react';
import React from 'react';

// We load this component dynamically to disable SSR on it because it depends on the value of the window object.
const AuthContextProvider = dynamic(
  () => {
    return import('../auth/AuthContext');
  },
  { ssr: false }
);

const errorFunction = (error: unknown) => {
  if (error instanceof ApiError) {
    console.error(error, {
      errorCode: error.errorCode,
      appName: error.appName,
      ctx: error.ctx,
      cause: error.cause,
      requestId: error.requestId,
      errorId: error.errorId,
    });
    toast.error(
      `Error: ${error.message}\nCódigo: ${error.errorCode}\n[${error.errorId}]`,
      {
        duration: 5000,
      }
    );
    return;
  }

  let realError = error;
  if (!(error instanceof Error)) {
    realError = new Error(JSON.stringify(error));
  }

  console.error(realError);
  toast.error(`Error: ${(realError as Error).message}`);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: errorFunction,
  }),
  mutationCache: new MutationCache({
    onError: errorFunction,
  }),
});

type ExtendedAppProps = AppProps & {
  Component: AppProps['Component'] & { skipAuth?: boolean };
};

function ComponentWrapper({ Component, pageProps }: ExtendedAppProps) {
  const user = useUser();

  const asideMenuOptions = useMemo(() => {
    return [
      {
        title: 'Aplicaciones',
        path: '/applications',
        icon: <DashboardIcon color="primary" />,
      },
    ];
  }, []);

  if (Component.skipAuth) {
    return (
      <React.StrictMode>
        <Component {...pageProps} />
      </React.StrictMode>
    );
  }

  // The AuthContextProvider (and the keycloak-js library) does not play well with StrictMode's unmounting and remounting of the components.
  // Running AuthContextProvider inside StrictMode causes an infinite redirect loop when coming back from the idP.
  return (
    <AuthContextProvider clientId="monorepo">
      <Policy
        user={user}
        roles={['login']}
        fallback={
          <Forbidden
            text={'No tienes permiso para acceder a la aplicación'}
            userLogout={user?.actions?.logout}
            email={user?.email}
          />
        }
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Layout
            pageTitle="App Distribute"
            userName={user?.name}
            noPageTitleHeader={true}
            customMainStyle={{
              top: '60px',
              minWidth: '410px',
              padding: theme.spacing(2),
            }}
            customHeaderStyle={{
              borderBottom: '1px solid #eeeeee',
              opacity: 1,
            }}
            asideMenuOptions={asideMenuOptions}
            customButtonsAsideStyle={{ height: 72 }}
            enabledUserIcon={true}
            userLogout={user?.actions?.logout}
          >
            <Component {...pageProps} />
          </Layout>
        </ThemeProvider>
      </Policy>
    </AuthContextProvider>
  );
}

function MyApp(props: ExtendedAppProps) {
  return (
    <ErrorBoundary FallbackComponent={() => <ErrorFallback />}>
      <AppNameProvider appName={APP_NAME}>
        <Provider>
          <Toaster />
          <QueryClientProvider client={queryClient}>
            <ComponentWrapper {...props} />
          </QueryClientProvider>
        </Provider>
      </AppNameProvider>
    </ErrorBoundary>
  );
}

export default MyApp;
